import { useState } from 'react';
import { Button, IconButton, Stack, TextField, Tooltip, MenuItem, Snackbar, Alert, Chip } from '@mui/material';
import { Add, Visibility, Delete } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../../components/PageHeader';
import { ExportMenu } from '../../../components/ExportMenu';
import { DataTable, type Column } from '../../../components/DataTable';
import { StatusBadge } from '../../../components/StatusBadge';
import { ConfirmDialog } from '../../../components/ConfirmDialog';
import { InstallationFormDialog } from './InstallationFormDialog';
import { InstallationDetailDialog } from './InstallationDetailDialog';
import { installationsApi, type Installation, type CreateInstallationInput, type InstallationStatus } from '../../../api/installations.api';
import { extractApiError } from '../../../api/error';
import { useAuth } from '../../../hooks/useAuth';

const STATUSES: InstallationStatus[] = ['requested', 'assigned', 'scheduled', 'in_progress', 'commissioned', 'signed_off', 'pending_handover', 'handed_over', 'closed', 'cancelled'];

export function InstallationListPage() {
  const qc = useQueryClient();
  const { hasPermission } = useAuth();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [status, setStatus] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [detailId, setDetailId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState<Installation | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['installations', page, limit, status],
    queryFn: () => installationsApi.list({ page, limit, status: status || undefined }),
  });
  const invalidate = () => qc.invalidateQueries({ queryKey: ['installations'] });

  const createMut = useMutation({
    mutationFn: (input: CreateInstallationInput) => installationsApi.create(input),
    onSuccess: (i) => { setFormOpen(false); invalidate(); setDetailId(i.id); },
    onError: (e) => setFormError(extractApiError(e)),
  });
  const deleteMut = useMutation({
    mutationFn: (id: number) => installationsApi.remove(id),
    onSuccess: () => { setDeleting(null); invalidate(); },
    onError: (e) => { setToast(extractApiError(e)); setDeleting(null); },
  });

  const columns: Column<Installation>[] = [
    { key: 'requestCode', label: 'Request', width: 170 },
    { key: 'dealerId', label: 'Dealer', render: (r) => `#${r.dealerId}` },
    { key: 'priority', label: 'Priority', render: (r) => <Chip size="small" label={r.priority} /> },
    { key: 'scheduledDate', label: 'Scheduled', render: (r) => r.scheduledDate ?? '—' },
    { key: 'slaBreached', label: 'SLA', render: (r) => (r.slaBreached ? <Chip size="small" color="error" label="breached" /> : 'OK') },
    { key: 'status', label: 'Status', render: (r) => <StatusBadge status={r.status} /> },
  ];

  return (
    <>
      <PageHeader
        title="Installations"
        subtitle="EVCPD installation lifecycle: request → assign → schedule → execute → commission → sign-off → handover → close."
        action={
          <Stack direction="row" spacing={1}>
            <ExportMenu dataset="installations" permission="installation.export" filters={{ status }} />
            {hasPermission('installation.create') && <Button startIcon={<Add />} variant="contained" onClick={() => { setFormError(null); setFormOpen(true); }}>New Installation</Button>}
          </Stack>
        }
      />
      <Stack direction="row" mb={2}>
        <TextField select size="small" label="Status" value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} sx={{ minWidth: 200 }}>
          <MenuItem value="">All</MenuItem>
          {STATUSES.map((s) => <MenuItem key={s} value={s}>{s.replace(/_/g, ' ')}</MenuItem>)}
        </TextField>
      </Stack>
      <DataTable
        columns={columns}
        rows={data?.data ?? []}
        loading={isLoading}
        page={page}
        limit={limit}
        total={data?.meta.total ?? 0}
        onPageChange={setPage}
        onLimitChange={(l) => { setLimit(l); setPage(1); }}
        rowKey={(r) => r.id}
        actions={(r) => (
          <>
            <Tooltip title="Open"><IconButton size="small" onClick={() => setDetailId(r.id)}><Visibility fontSize="small" /></IconButton></Tooltip>
            {hasPermission('installation.delete') && ['requested', 'cancelled'].includes(r.status) && (
              <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => setDeleting(r)}><Delete fontSize="small" /></IconButton></Tooltip>
            )}
          </>
        )}
      />

      <InstallationFormDialog open={formOpen} error={formError} onClose={() => setFormOpen(false)} onSubmit={async (input) => { setFormError(null); await createMut.mutateAsync(input); }} />
      <InstallationDetailDialog installationId={detailId} onClose={() => setDetailId(null)} onChanged={invalidate} />
      <ConfirmDialog open={Boolean(deleting)} title="Delete installation" message={`Delete ${deleting?.requestCode}?`} confirmLabel="Delete" onCancel={() => setDeleting(null)} onConfirm={() => deleting && deleteMut.mutate(deleting.id)} />
      <Snackbar open={Boolean(toast)} autoHideDuration={6000} onClose={() => setToast(null)}><Alert severity="warning" onClose={() => setToast(null)}>{toast}</Alert></Snackbar>
    </>
  );
}
